import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, computed, DestroyRef, inject, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import dayjs, { Dayjs } from 'dayjs';
import { catchError, filter, finalize, forkJoin, map, of, startWith } from 'rxjs';
import { CheckboxSelectorDialogComponent } from 'src/app/components/main/shared/checkbox-selector-dialog/checkbox-selector-dialog.component';
import { SelectorDialogData, SelectorDialogDataRow } from 'src/app/components/main/shared/selector-dialog/selector-dialog.component';
import { TextDialogComponent } from 'src/app/components/main/shared/text-dialog/text-dialog.component';
import { DayOfWeekEnum, DayOfWeekWithLabels, dayOfWeekIso, workingDays } from 'src/app/enums/day-of-week.enum';
import { ScheduleMode } from 'src/app/enums/schedule-mode.enum';
import { BisualSetting } from 'src/app/models/bisual-setting.model';
import { EmployeeCalendarRegisterWithRelationsSerialized } from 'src/app/models/employee_calendar_register.model';
import { WorkingContractWeeklyScheduleExceptionRequestDayPeriod } from 'src/app/models/working-contract-weekly-schedule-exception-request.model';
import { ConfigureWorkingScheduleDialogData } from 'src/app/models/working_contract.model';
import { WorkingContractWeeklySchedule } from 'src/app/models/working_contract_weekly_schedule.model';
import { HourDecimalToHoursMinutesPipe } from 'src/app/pipes/hour-decimal-to-hours-minutes.pipe';
import { ApiService } from 'src/app/services';
import { ScheduleExceptionRequestReasonDialogComponent } from './schedule-exception-request-reason-dialog/schedule-exception-request-reason-dialog.component';

@Component({
  selector: 'app-configure-working-schedule-dialog',
  templateUrl: './configure-working-schedule-dialog.component.html',
  styleUrl: './configure-working-schedule-dialog.component.css',
  imports: [
    HourDecimalToHoursMinutesPipe,
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDialogActions,
    MatButton,
    MatIcon,
    MatCheckbox,
    MatSlideToggle,
    MatIconButton,
    MatTooltip,
  ],
})
export class ConfigureWorkingScheduleDialogComponent {
  // Dependencies
  private readonly dialog_ref = inject(MatDialogRef<ConfigureWorkingScheduleDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  readonly data = inject<ConfigureWorkingScheduleDialogData>(MAT_DIALOG_DATA);

  // Mode / dialog metadata
  readonly schedule_mode = computed<ScheduleMode>(() => this.data.schedule_mode);

  readonly is_normal_mode = computed(() => this.schedule_mode() === ScheduleMode.Normal);
  readonly is_week_exception_mode = computed(() => this.schedule_mode() === ScheduleMode.WeekException);
  readonly is_creating_exception_for_this_week = computed(
    () => this.is_week_exception_mode() && this.data.start_week_date != null && this.data.end_week_date != null
  );

  readonly is_current_week_exception = computed((): boolean => {
    if (!this.is_week_exception_mode() || !this.data.start_week_date) {
      return false;
    }

    return dayjs(this.data.start_week_date).startOf('day').isSame(dayjs().startOf('isoWeek'), 'day');
  });

  readonly is_past_week_exception = computed((): boolean => {
    if (!this.is_week_exception_mode() || !this.data.start_week_date) {
      return false;
    }

    return dayjs(this.data.start_week_date).startOf('day').isBefore(dayjs().startOf('isoWeek'), 'day');
  });

  readonly dialog_title = computed(() => {
    return this.data.dialog_title;
  });

  readonly dialog_subtitle = computed(() => {
    return this.data.dialog_subtitle;
  });

  // Form state
  form!: FormGroup<Record<DayOfWeekEnum, FormArray>>;
  readonly days_config = DayOfWeekWithLabels;
  readonly saving = signal(false);

  readonly free_form_mode = signal(false);
  readonly can_use_free_form_mode = computed(() => this.is_current_week_exception() || this.is_past_week_exception());
  readonly is_free_form_active = computed((): boolean => this.can_use_free_form_mode() && this.free_form_mode());

  readonly remote_work_days_control = this.fb.control<DayOfWeekEnum[]>([], { nonNullable: true });
  readonly initial_schedule_snapshot = signal('');
  readonly remote_work_days = toSignal(this.remote_work_days_control.valueChanges.pipe(startWith(this.remote_work_days_control.value)), {
    initialValue: [] as DayOfWeekEnum[],
  });
  readonly selected_remote_work_days = computed(() => this.remote_work_days());
  private form_value_signal!: Signal<FormValue>;
  readonly current_schedule_snapshot = computed(() => {
    this.form_value_signal();
    this.selected_remote_work_days();
    return this.buildScheduleSnapshot();
  });

  // Settings / async loading
  private readonly settings_request_completed = signal(false);

  readonly daily_working_hours_settings = toSignal(
    forkJoin({
      min: this.api.getBisualSetting('minimum-working-hours-to-work-per-day'),
      max: this.api.getBisualSetting('maximum-working-hours-to-work-per-day'),
      end_time_grace_minutes: this.api.getBisualSetting('working-contract-weekly-schedule-exception-end-time-grace-minutes'),
      expected_hours_by_day: this.api.getUserExpectedHoursByDay(this.data.working_contract.user_id, this.data.start_week_date),
    }).pipe(
      catchError(() => {
        this.snack_bar.open('No se pudieron cargar los ajustes de horario.', '', { duration: 3000 });
        return of(null);
      }),
      finalize(() => this.settings_request_completed.set(true))
    ),
    {
      initialValue: null as {
        min: BisualSetting;
        max: BisualSetting;
        end_time_grace_minutes: BisualSetting;
        expected_hours_by_day: number[];
      } | null,
    }
  );

  readonly loading = computed(() => !this.settings_request_completed());
  readonly settings_error = computed(() => this.settings_request_completed() && this.daily_working_hours_settings() === null);

  // Expected hours / totals
  readonly hours_per_week_expected_raw = computed((): number | null => {
    return this.data.working_contract.current_working_contract_variable_condition?.hours_per_week_expected ?? null;
  });

  readonly hours_per_week_expected = computed((): number | null => {
    if (this.is_normal_mode()) {
      return this.hours_per_week_expected_raw();
    }

    const expected_minutes = this.getExpectedWeeklyMinutesFromSettings();
    if (expected_minutes !== null) {
      return expected_minutes / 60;
    }

    return this.hours_per_week_expected_raw();
  });

  readonly hours_per_day = computed((): Record<DayOfWeekEnum, number> => this.computeHoursPerDayFromFormValue(this.form_value_signal()));

  readonly weekly_hours_configured = computed((): number => {
    return this.getConfiguredWeeklyMinutes() / 60;
  });

  readonly weekly_hours_total_correct = computed((): boolean => {
    const expected_minutes = this.getExpectedWeeklyMinutesFromSettings();
    if (expected_minutes === null) {
      return false;
    }

    return expected_minutes === this.getConfiguredWeeklyMinutes();
  });

  // Daily constraints
  readonly min_working_hours_per_day = computed((): number | null => {
    const settings = this.daily_working_hours_settings();
    if (settings == null) return null;
    const raw_min = Number(settings.min.value);
    const expected = this.hours_per_week_expected_raw();
    if (expected == null) return null;
    return (expected * raw_min) / 40;
  });

  readonly max_working_hours_per_day = computed((): number | null => {
    const settings = this.daily_working_hours_settings();
    if (settings == null) return null;
    const raw_max = Number(settings.max.value);
    const expected = this.hours_per_week_expected_raw();
    if (expected == null) return null;
    return (expected * raw_max) / 40;
  });

  readonly end_time_grace_minutes = computed<number>(() => {
    const settings = this.daily_working_hours_settings();
    return Number(settings?.end_time_grace_minutes?.value);
  });

  // Validation
  readonly day_hours_out_of_range = computed(() => {
    const min_h = this.min_working_hours_per_day();
    const max_h = this.max_working_hours_per_day();
    if (min_h == null || max_h == null || this.is_free_form_active()) return {};
    const hours_per_day = this.hours_per_day();
    const out: Partial<Record<DayOfWeekEnum, boolean>> = {};
    for (const day of this.days_config) {
      if (!this.shouldValidateDay(day.key)) {
        out[day.key] = false;
        continue;
      }
      const h = hours_per_day[day.key] ?? 0;
      out[day.key] = h > 0 && (h < min_h || h > max_h);
    }
    return out;
  });

  readonly has_at_least_one_period_per_working_day = computed(() => {
    const value = this.form_value_signal();
    if (value === null || value === undefined || typeof value !== 'object') return false;
    if (this.is_free_form_active()) {
      return this.days_config.some(
        day => this.shouldIncludeDayInPayload(day.key) && Array.isArray(value[day.key]) && value[day.key].length > 0
      );
    }
    for (const day of this.days_config) {
      if (!this.shouldRequirePeriodsForDay(day.key)) {
        continue;
      }
      const periods = value[day.key];
      if (!Array.isArray(periods) || periods.length === 0) return false;
    }
    return true;
  });

  readonly allowed_remote_work_days_limit = computed<number>((): number => {
    return this.data.allowed_remote_work_days_resolved;
  });

  readonly has_valid_remote_work_days_selection = computed(() => {
    if (!this.is_normal_mode()) {
      return true;
    }

    const allowed_days = this.allowed_remote_work_days_limit();
    const selected_count = this.selected_remote_work_days().length;
    return selected_count <= allowed_days;
  });

  readonly has_valid_hours_per_day = computed(() => {
    const min_h = this.min_working_hours_per_day();
    const max_h = this.max_working_hours_per_day();
    if (min_h == null || max_h == null || this.is_free_form_active()) return true;
    const hours_per_day = this.hours_per_day();
    for (const day of this.days_config) {
      if (!this.shouldValidateDay(day.key)) {
        continue;
      }
      const h = hours_per_day[day.key] ?? 0;
      if (h > 0 && (h < min_h || h > max_h)) return false;
    }
    return true;
  });

  readonly day_time_ranges_invalid = computed(() => this.computeDaysWithInvalidTimeRanges(this.form_value_signal()));
  readonly day_has_past_time_inputs = computed(() => this.computeDaysWithPastTimeInputsForToday(this.form_value_signal()));
  readonly has_valid_today_time_inputs = computed(() => !Object.values(this.day_has_past_time_inputs()).some(Boolean));

  readonly has_valid_time_ranges = computed(() => {
    const invalid = this.day_time_ranges_invalid();
    return !Object.values(invalid).some(Boolean);
  });

  readonly has_unsaved_changes = computed(() => {
    return this.current_schedule_snapshot() !== this.initial_schedule_snapshot();
  });

  readonly week_blocked_meeting_ecrs = computed(() => this.data.week_blocked_meeting_ecrs ?? []);
  readonly week_absence_days = computed(() => this.data.week_absence_days ?? []);
  readonly uncovered_blocked_meeting_conflicts = computed(() => this.getUncoveredBlockedMeetingConflicts());
  readonly has_uncovered_blocked_meetings = computed(() => this.uncovered_blocked_meeting_conflicts().length > 0);
  readonly blocked_meetings_warning_message = computed(() => {
    if (!this.has_uncovered_blocked_meetings()) {
      return '';
    }

    const labels_by_day = new Map(this.days_config.map(day => [day.key, day.shortLabel]));
    const first_conflicts = this.uncovered_blocked_meeting_conflicts()
      .slice(0, 3)
      .map(conflict => `${labels_by_day.get(conflict.day_key) ?? conflict.day_key} ${conflict.from_time}-${conflict.to_time}`);
    const suffix = this.uncovered_blocked_meeting_conflicts().length > 3 ? '...' : '';
    return `Tienes reuniones programadas fuera de las franjas configuradas (${first_conflicts.join(', ')}${suffix}).`;
  });

  // Submit state
  readonly can_submit = computed(() => {
    if (this.is_free_form_active()) {
      return this.has_at_least_one_period_per_working_day() && this.has_valid_time_ranges() && this.has_unsaved_changes();
    }

    const base =
      this.has_at_least_one_period_per_working_day() &&
      this.has_valid_hours_per_day() &&
      this.has_valid_time_ranges() &&
      this.has_valid_today_time_inputs();
    const has_expected_weekly_hours = this.hours_per_week_expected() !== null;
    const total_correct = this.weekly_hours_total_correct();
    if (this.is_normal_mode()) {
      return (
        base && has_expected_weekly_hours && total_correct && this.has_valid_remote_work_days_selection() && this.has_unsaved_changes()
      );
    }
    return base && has_expected_weekly_hours && total_correct && this.has_unsaved_changes();
  });

  readonly save_button_disabled = computed((): boolean => this.loading() || this.settings_error() || this.saving() || !this.can_submit());

  // Tooltips
  readonly save_button_tooltip = computed(() => {
    if (this.loading() || this.saving()) return null;
    if (!this.has_unsaved_changes()) return 'No se han realizado cambios';
    if (this.is_free_form_active()) {
      if (!this.has_at_least_one_period_per_working_day()) return 'Añade al menos una franja horaria';
      if (!this.has_valid_time_ranges()) return 'Revisa la configuración de las franjas horarias';
      return null;
    }
    if (!this.has_at_least_one_period_per_working_day()) return 'Añade al menos una franja horaria en cada día de lunes a viernes';
    if (!this.has_valid_time_ranges()) return 'Revisa la configuración de las franjas horarias';
    if (!this.has_valid_today_time_inputs()) return 'No puedes usar horas anteriores a la hora actual en el día de hoy';
    if (!this.has_valid_hours_per_day()) return 'Revisa las horas asignadas a cada día';
    if (this.hours_per_week_expected() === null) return 'No se pudieron calcular las horas esperadas';
    if (!this.weekly_hours_total_correct()) return 'La suma total de horas no coincide con lo esperado';
    if (this.is_normal_mode() && !this.has_valid_remote_work_days_selection()) {
      const allowed_days = this.allowed_remote_work_days_limit();
      const selected_count = this.selected_remote_work_days().length;
      if (allowed_days <= 0 && selected_count > 0) {
        return 'No tienes días de teletrabajo permitidos en tu condición actual';
      }
      if (allowed_days > 0) {
        return `Puedes seleccionar como máximo ${allowed_days} día(s) de teletrabajo (ahora: ${selected_count})`;
      }
    }
    return null;
  });

  readonly time_ranges_validation_tooltip = computed(() => 'Cada franja debe tener entrada < salida y no solaparse con la anterior');

  readonly day_hours_range_tooltip = computed(() => {
    const min = this.min_working_hours_per_day();
    const max = this.max_working_hours_per_day();
    if (min == null || max == null) return '';
    return `Cada día debe tener entre ${min}h y ${max}h`;
  });

  isDayPastToday(day_key: DayOfWeekEnum): boolean {
    // Nunca será pasado ya que sólo se pueden editar a futuro
    if (this.is_normal_mode()) {
      return false;
    }

    if (this.is_week_exception_mode() && this.data.start_week_date) {
      const week_start = dayjs(this.data.start_week_date).startOf('day');
      const day_offset = dayOfWeekIso(day_key) - 1;
      const that_day_in_week = week_start.add(day_offset, 'day').startOf('day');
      const today = dayjs().startOf('day');
      return today.isAfter(that_day_in_week);
    }

    return dayjs().isAfter(dayjs().day(dayOfWeekIso(day_key)));
  }

  isDayDisabledInWeekException(day_key: DayOfWeekEnum): boolean {
    if (!this.is_week_exception_mode()) {
      return false;
    }

    if (this.is_free_form_active()) {
      return false;
    }

    if (this.isDayZeroExpectedInWeekException(day_key)) {
      return true;
    }

    return this.isDayPastToday(day_key);
  }

  isPeriodStartLockedInWeekException(day_key: DayOfWeekEnum, period_index: number): boolean {
    if (this.isDayDisabledInWeekException(day_key)) {
      return true;
    }

    if (this.is_free_form_active()) {
      return false;
    }

    // New franjas that are already fully in the past must stay editable so the user can fix them.
    if (this.isNewPeriod(day_key, period_index) && this.isCurrentDayEndedPeriod(day_key, period_index)) {
      return false;
    }

    if (this.isCurrentDayEndedPeriod(day_key, period_index)) {
      return true;
    }

    return this.isCurrentDayStartedPeriod(day_key, period_index);
  }

  isPeriodEndLockedInWeekException(day_key: DayOfWeekEnum, period_index: number): boolean {
    if (this.isDayDisabledInWeekException(day_key)) {
      return true;
    }

    if (this.is_free_form_active()) {
      return false;
    }

    if (this.isCurrentDayInWeekException(day_key)) {
      return false;
    }

    if (this.isNewPeriod(day_key, period_index) && this.isCurrentDayEndedPeriod(day_key, period_index)) {
      return false;
    }

    return this.isCurrentDayEndedPeriod(day_key, period_index, true);
  }

  isPeriodDeleteLockedInWeekException(day_key: DayOfWeekEnum, period_index: number): boolean {
    if (!this.is_week_exception_mode()) {
      return false;
    }

    if (this.isDayDisabledInWeekException(day_key)) {
      return true;
    }

    if (this.free_form_mode()) {
      return false;
    }

    if (this.isNewPeriod(day_key, period_index)) {
      return false;
    }

    return this.isCurrentDayStartedPeriod(day_key, period_index);
  }

  private isDayZeroExpectedInWeekException(day_key: DayOfWeekEnum): boolean {
    if (!this.is_week_exception_mode()) {
      return false;
    }

    const settings = this.daily_working_hours_settings();
    const by_day = settings?.expected_hours_by_day;
    if (!Array.isArray(by_day) || by_day.length !== 7) {
      return false;
    }

    const day_index = dayOfWeekIso(day_key) - 1;
    return (by_day[day_index] ?? 0) === 0;
  }

  private shouldCountDayInWeeklyTotals(day_key: DayOfWeekEnum): boolean {
    return this.shouldIncludeDayInPayload(day_key);
  }

  private shouldRequirePeriodsForDay(day_key: DayOfWeekEnum): boolean {
    if (this.is_normal_mode()) {
      return workingDays.includes(day_key);
    }

    if (!this.is_week_exception_mode()) {
      return false;
    }

    // WeekException: se requieren periodos de lunes a viernes, excepto si ese día tiene 0 horas esperadas.
    return workingDays.includes(day_key) && !this.isDayZeroExpectedInWeekException(day_key);
  }

  private shouldValidateDay(day_key: DayOfWeekEnum): boolean {
    if (!this.is_week_exception_mode()) {
      return true;
    }

    return !this.isDayDisabledInWeekException(day_key);
  }

  private shouldIncludeDayInPayload(day_key: DayOfWeekEnum): boolean {
    if (!this.is_week_exception_mode()) {
      return true;
    }

    if (this.is_free_form_active()) {
      return true;
    }

    return !this.isDayZeroExpectedInWeekException(day_key);
  }

  private setPeriodControlsDisabledForDay(periodGroup: FormGroup, day_key: DayOfWeekEnum): void {
    const disabled = this.isDayDisabledInWeekException(day_key);
    const start = periodGroup.get('start_time');
    const end = periodGroup.get('end_time');
    if (disabled) {
      start?.disable();
      end?.disable();
    } else {
      start?.enable();
      end?.enable();
    }
  }

  private syncDisabledControlsFromAvailability(): void {
    if (!this.is_week_exception_mode()) {
      return;
    }

    for (const day of this.days_config) {
      const periods_arr = this.getPeriods(day.key);
      for (let period_index = 0; period_index < periods_arr.controls.length; period_index++) {
        const ctrl = periods_arr.controls[period_index];
        this.setPeriodControlsDisabledForDay(ctrl as FormGroup, day.key);
        this.syncPeriodStartedLock(ctrl as FormGroup, day.key, period_index);
      }
    }
  }

  constructor() {
    this.form = this.fb.group({
      monday: this.fb.array([]),
      tuesday: this.fb.array([]),
      wednesday: this.fb.array([]),
      thursday: this.fb.array([]),
      friday: this.fb.array([]),
      saturday: this.fb.array([]),
      sunday: this.fb.array([]),
    });
    const raw_value = (): FormValue => this.form.getRawValue() as FormValue;
    this.form_value_signal = toSignal(this.form.valueChanges.pipe(startWith(null), map(raw_value)), { initialValue: raw_value() });

    this.fillWeekExceptionFormFromInitialData();

    if (this.is_normal_mode() && this.data.weekly_schedule != null) {
      this.fillFormFromSchedule(this.data.weekly_schedule);
    }

    this.initial_schedule_snapshot.set(this.buildScheduleSnapshot());

    toObservable(this.settings_request_completed)
      .pipe(
        filter(completed => completed),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(() => this.syncDisabledControlsFromAvailability());
  }

  private fillWeekExceptionFormFromInitialData(): void {
    if (!this.is_week_exception_mode()) {
      return;
    }

    const exception = this.data.weekly_schedule_exception;
    if (exception != null) {
      this.fillFormFromDayPeriods(exception.working_contract_weekly_schedule_exception_day_periods);
    } else if (this.data.weekly_schedule != null) {
      this.fillFormFromDayPeriods(this.data.weekly_schedule.working_contract_weekly_schedule_day_periods);
    }
  }

  onFreeFormModeChange(event: MatSlideToggleChange): void {
    if (!this.can_use_free_form_mode()) {
      this.free_form_mode.set(false);
      this.syncDisabledControlsFromAvailability();
      return;
    }

    this.free_form_mode.set(event.checked);
    if (!event.checked) {
      this.fillWeekExceptionFormFromInitialData();
      this.syncDisabledControlsFromAvailability();
      return;
    }

    this.applyLatestRequestProposedPeriodsToFreeForm();
    this.syncDisabledControlsFromAvailability();
  }

  private applyLatestRequestProposedPeriodsToFreeForm(): void {
    const exception_id = this.data.weekly_schedule_exception?.id;
    if (exception_id == null) {
      return;
    }

    this.api
      .getWorkingContractWeeklyScheduleExceptionRequests({
        user_id: this.data.working_contract.user_id,
        working_contract_weekly_schedule_exception_id: exception_id,
        with: 'working_contract_weekly_schedule_exception_request_day_periods',
        order_by: 'created_at:desc',
        per_page: 1,
        page: 1,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(response => {
        if (!this.is_free_form_active() || response == null) {
          return;
        }

        const latest_request = response.data[0];
        if (latest_request == null) {
          return;
        }

        const proposed_periods = (latest_request.working_contract_weekly_schedule_exception_request_day_periods ?? [])
          .filter((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): boolean => period.side === 'proposed')
          .map((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod) => ({
            day_of_week: period.day_of_week,
            start_time: period.start_time,
            end_time: period.end_time,
          }));

        if (proposed_periods.length === 0) {
          return;
        }

        this.fillFormFromDayPeriods(proposed_periods);
        this.syncDisabledControlsFromAvailability();
      });
  }

  // Form access
  getPeriods(day_key: DayOfWeekEnum): FormArray {
    return this.form.get(day_key) as FormArray;
  }

  // Period actions
  addPeriod(day_key: DayOfWeekEnum): void {
    const group = this.fb.group({
      start_time: [null, Validators.required],
      end_time: [null, Validators.required],
      is_new: [true],
      original_end_time: [null as string | null],
    });
    this.setPeriodControlsDisabledForDay(group, day_key);
    this.getPeriods(day_key).push(group);
    const last_index = this.getPeriods(day_key).length - 1;
    this.syncPeriodStartedLock(group, day_key, last_index);
  }

  removePeriod(day_key: DayOfWeekEnum, index: number): void {
    if (this.isPeriodDeleteLockedInWeekException(day_key, index)) {
      return;
    }

    this.getPeriods(day_key).removeAt(index);
  }

  // Day copy
  copyDay(source_day_key: DayOfWeekEnum): void {
    const source_label = this.days_config.find(d => d.key === source_day_key)?.label ?? source_day_key;
    const target_rows: SelectorDialogDataRow[] = this.days_config.map(d => ({
      value: d.key,
      textToShow: d.label,
      disabled: this.isDayDisabledInWeekException(d.key) || this.dayHasStartedPeriodLocked(d.key) || d.key === source_day_key,
    }));
    if (target_rows.length === 0) return;

    const selector_data: SelectorDialogData = {
      dialog_title: `Copiar horario de ${source_label} a...`,
      dialog_ok_button: 'Copiar',
      dialog_cancel_button: 'Cancelar',
      data: target_rows,
    };

    const ref = this.dialog.open(CheckboxSelectorDialogComponent, {
      width: '400px',
      data: selector_data,
    });

    ref.afterClosed().subscribe((selected_day_keys: DayOfWeekEnum[] | undefined) => {
      if (selected_day_keys === undefined || selected_day_keys === null || selected_day_keys.length === 0) return;
      const keys_to_apply = this.is_week_exception_mode()
        ? selected_day_keys.filter(key => !this.isDayDisabledInWeekException(key))
        : selected_day_keys;
      for (const target_key of keys_to_apply) {
        this.applyCopyDay(source_day_key, target_key);
      }
      if (keys_to_apply.length === 0) return;
      const message =
        keys_to_apply.length === 1
          ? `Horario copiado a ${this.days_config.find(d => d.key === keys_to_apply[0])?.label ?? keys_to_apply[0]}`
          : `Horario copiado a ${keys_to_apply.length} días`;
      this.snack_bar.open(message, '', { duration: 2000 });
    });
  }

  toggleRemoteWorkDay(day_key: DayOfWeekEnum, checked: boolean): void {
    const current = [...this.selected_remote_work_days()];

    if (checked) {
      if (!current.includes(day_key)) {
        current.push(day_key);
      }
    } else {
      const idx = current.indexOf(day_key);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
    }

    this.remote_work_days_control.setValue(current);
  }

  isRemoteWorkDaySelected(day_key: DayOfWeekEnum): boolean {
    return this.selected_remote_work_days().includes(day_key);
  }

  isRemoteWorkDayDisabled(day_key: DayOfWeekEnum): boolean {
    if (!this.is_normal_mode()) {
      return false;
    }

    const selected = this.selected_remote_work_days();
    const max_days = this.allowed_remote_work_days_limit();
    const is_selected = selected.includes(day_key);

    return !is_selected && selected.length >= max_days;
  }

  submit(): void {
    if (this.is_free_form_active()) {
      this.requestScheduleChange();
      return;
    }

    this.save();
  }

  private requestScheduleChange(): void {
    if (this.saving() || !this.can_submit()) {
      return;
    }

    this.dialog
      .open<ScheduleExceptionRequestReasonDialogComponent, void, string>(ScheduleExceptionRequestReasonDialogComponent, {
        width: '480px',
        maxWidth: '95vw',
        autoFocus: 'textarea',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((reason: string | undefined): void => {
        if (reason === undefined || reason.trim() === '') {
          return;
        }

        this.saving.set(true);
        this.api
          .createWorkingContractWeeklyScheduleExceptionRequest({
            working_contract_id: this.data.working_contract.id,
            start_week_date: this.data.start_week_date,
            end_week_date: this.data.end_week_date,
            reason,
            day_periods: this.buildDayPeriods(),
          })
          .subscribe({
            next: (response): void => {
              this.saving.set(false);
              this.dialog.open(TextDialogComponent, {
                data: {
                  title: 'Solicitud enviada',
                  body: response.message,
                  dialog_close_button: 'Entendido',
                },
              });
              this.dialog_ref.close(false);
            },
            error: (): void => {
              this.saving.set(false);
            },
          });
      });
  }

  save(): void {
    if (this.hasPastTimeInputInTodayNow()) {
      this.snack_bar.open('No puedes usar horas anteriores a la hora actual en el día de hoy.', '', { duration: 3000 });
      return;
    }

    const day_periods = this.buildDayPeriods();
    this.saving.set(true);

    const on_success = (): void => {
      this.saving.set(false);
      this.dialog_ref.close(true);
    };

    const on_error = (): void => {
      this.saving.set(false);
    };

    if (this.is_normal_mode()) {
      const selected_remote_days = this.selected_remote_work_days();
      this.api
        .storeWorkingContractWeeklySchedule(
          this.data.working_contract.id,
          {
            day_periods,
            has_remote_work_monday: selected_remote_days.includes(DayOfWeekEnum.Monday),
            has_remote_work_tuesday: selected_remote_days.includes(DayOfWeekEnum.Tuesday),
            has_remote_work_wednesday: selected_remote_days.includes(DayOfWeekEnum.Wednesday),
            has_remote_work_thursday: selected_remote_days.includes(DayOfWeekEnum.Thursday),
            has_remote_work_friday: selected_remote_days.includes(DayOfWeekEnum.Friday),
            has_remote_work_saturday: selected_remote_days.includes(DayOfWeekEnum.Saturday),
            has_remote_work_sunday: selected_remote_days.includes(DayOfWeekEnum.Sunday),
          },
          this.data.start_week_date
        )
        .subscribe({ next: on_success, error: on_error });
    } else {
      this.api
        .updateOrCreateWorkingContractWeeklyScheduleException(
          this.data.working_contract.id,
          {
            start_week_date: this.data.start_week_date,
            end_week_date: this.data.end_week_date,
            day_periods,
          },
          this.data.start_week_date
        )
        .subscribe({ next: on_success, error: on_error });
    }
  }

  // Copy helpers
  private applyCopyDay(source_day_key: DayOfWeekEnum, target_day_key: DayOfWeekEnum): void {
    if (this.dayHasStartedPeriodLocked(target_day_key)) {
      return;
    }

    const source_periods = this.getPeriods(source_day_key);
    const target_periods = this.getPeriods(target_day_key);
    const values: { start_time: string; end_time: string }[] = source_periods.controls.map((ctrl: unknown) => {
      const g = ctrl as FormGroup;
      return {
        start_time: g.get('start_time')?.value ?? '09:00',
        end_time: g.get('end_time')?.value ?? '13:00',
      };
    });
    target_periods.clear();
    for (const v of values) {
      const group = this.fb.group({
        start_time: [v.start_time, Validators.required],
        end_time: [v.end_time, Validators.required],
        is_new: [true],
        original_end_time: [null as string | null],
      });
      this.setPeriodControlsDisabledForDay(group, target_day_key);
      target_periods.push(group);
    }
  }

  // Payload builders
  private buildDayPeriods(): { day_of_week: DayOfWeekEnum; start_time: string; end_time: string }[] {
    const result: { day_of_week: DayOfWeekEnum; start_time: string; end_time: string }[] = [];
    for (const day of this.days_config) {
      if (!this.shouldIncludeDayInPayload(day.key)) {
        continue;
      }
      const periods_arr = this.getPeriods(day.key);
      for (let i = 0; i < periods_arr.length; i++) {
        const period_group = periods_arr.at(i) as FormGroup;
        const start_time = this.normalizeTimeToHHmm(period_group.get('start_time')?.value ?? '');
        const end_time = this.normalizeTimeToHHmm(period_group.get('end_time')?.value ?? '');
        if (start_time && end_time) {
          result.push({ day_of_week: day.key, start_time, end_time });
        }
      }
    }
    return result;
  }

  // Time parsing/normalization
  private normalizeTimeToHHmm(value: string): string {
    if (value === null || value === undefined || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    const parsed = dayjs(trimmed, ['HH:mm', 'H:mm', 'HH:m', 'H:m'], true);
    return parsed.isValid() ? parsed.format('HH:mm') : trimmed.slice(0, 5);
  }

  private timeToMinutes(timeStr: string): number {
    if (timeStr == null || typeof timeStr !== 'string' || !timeStr.trim()) return Number.NaN;
    const parts = timeStr.trim().split(':');
    if (parts.length < 2) return Number.NaN;
    const hours = Number.parseInt(parts[0], 10);
    const minutes = Number.parseInt(parts[1], 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return Number.NaN;
    return hours * 60 + minutes;
  }

  // Validation/calculation helpers
  private computeDaysWithInvalidTimeRanges(value: FormValue): Partial<Record<DayOfWeekEnum, boolean>> {
    const result: Partial<Record<DayOfWeekEnum, boolean>> = {};
    if (value == null || typeof value !== 'object') return result;
    for (const day of this.days_config) {
      if (!this.shouldValidateDay(day.key)) {
        continue;
      }
      const periods = value[day.key];
      if (!Array.isArray(periods) || periods.length === 0) continue;
      const with_minutes = periods.map(p => ({
        start: this.timeToMinutes(p?.start_time ?? ''),
        end: this.timeToMinutes(p?.end_time ?? ''),
      }));
      const any_invalid_parse = with_minutes.some(m => Number.isNaN(m.start) || Number.isNaN(m.end));
      if (any_invalid_parse) {
        result[day.key] = true;
        continue;
      }
      const sorted = [...with_minutes].sort((a, b) => a.start - b.start);
      let invalid = false;
      for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].start >= sorted[i].end) {
          invalid = true;
          break;
        }
        if (i > 0 && sorted[i].start < sorted[i - 1].end) {
          invalid = true;
          break;
        }
      }
      if (invalid) result[day.key] = true;
    }
    return result;
  }

  private getExpectedWeeklyMinutesFromSettings(): number | null {
    if (this.is_normal_mode()) {
      const expected = this.hours_per_week_expected_raw();
      return expected != null ? Math.round(expected * 60) : null;
    }

    const settings = this.daily_working_hours_settings();
    const by_day = settings?.expected_hours_by_day;
    if (!Array.isArray(by_day) || by_day.length === 0) {
      const expected = this.hours_per_week_expected_raw();
      return expected != null ? Math.round(expected * 60) : null;
    }

    return by_day.reduce((sum, hours) => sum + Math.round(hours * 60), 0);
  }

  private getConfiguredWeeklyMinutes(): number {
    return this.getTotalMinutesFromFormValue(this.form_value_signal());
  }

  private getTotalMinutesFromFormValue(value: FormValue): number {
    let total_minutes = 0;

    for (const day of this.days_config) {
      if (!this.shouldCountDayInWeeklyTotals(day.key)) {
        continue;
      }

      const periods = value[day.key];

      if (!Array.isArray(periods)) continue;

      for (const period of periods) {
        const start_str = period?.start_time ?? '';
        const end_str = period?.end_time ?? '';

        if (start_str && end_str) {
          const [start_hour, start_min] = start_str.split(':').map(Number);
          const [end_hour, end_min] = end_str.split(':').map(Number);
          total_minutes += end_hour * 60 + end_min - (start_hour * 60 + start_min);
        }
      }
    }

    return total_minutes;
  }

  private computeHoursPerDayFromFormValue(value: FormValue): Record<DayOfWeekEnum, number> {
    const result = {} as Record<DayOfWeekEnum, number>;

    for (const day of this.days_config) {
      result[day.key] = 0;
    }

    for (const day of this.days_config) {
      if (!this.shouldCountDayInWeeklyTotals(day.key)) {
        result[day.key] = 0;
        continue;
      }

      const periods = value[day.key];

      if (!Array.isArray(periods)) {
        result[day.key] = 0;
        continue;
      }

      let day_minutes = 0;

      for (const period of periods) {
        const start_str = period?.start_time ?? '';
        const end_str = period?.end_time ?? '';

        if (start_str && end_str) {
          const [start_hour, start_min] = start_str.split(':').map(Number);
          const [end_hour, end_min] = end_str.split(':').map(Number);
          day_minutes += end_hour * 60 + end_min - (start_hour * 60 + start_min);
        }
      }

      result[day.key] = day_minutes / 60;
    }

    return result;
  }

  private getUncoveredBlockedMeetingConflicts(): MeetingCoverageConflict[] {
    if (!this.is_week_exception_mode()) {
      return [];
    }

    const conflicts: MeetingCoverageConflict[] = [];
    const week_start = dayjs(this.data.start_week_date).startOf('day');
    const week_end = dayjs(this.data.end_week_date).endOf('day');
    const form_value = this.form_value_signal();

    for (const ecr of this.week_blocked_meeting_ecrs()) {
      const maybe_conflict = this.getMeetingCoverageConflictForEcr(ecr, form_value, week_start, week_end);
      if (maybe_conflict !== null) {
        conflicts.push(maybe_conflict);
      }
    }

    return conflicts;
  }

  private getMeetingCoverageConflictForEcr(
    ecr: EmployeeCalendarRegisterWithRelationsSerialized,
    form_value: FormValue,
    week_start: Dayjs,
    week_end: Dayjs
  ): MeetingCoverageConflict | null {
    const meeting_start = dayjs(ecr.from_date);
    const meeting_end = dayjs(ecr.to_date);

    if (!meeting_start.isValid() || !meeting_end.isValid()) {
      return null;
    }

    const starts_inside_week = meeting_start.isSame(week_start) || meeting_start.isAfter(week_start);
    const ends_inside_week = meeting_end.isSame(week_end) || meeting_end.isBefore(week_end);
    if (!starts_inside_week || !ends_inside_week || !meeting_start.isBefore(meeting_end)) {
      return null;
    }

    const day_index = meeting_start.isoWeekday();
    const day = this.days_config.find(day_config => dayOfWeekIso(day_config.key) === day_index);
    if (day == null) {
      return null;
    }

    if (this.week_absence_days().includes(day.key)) {
      return null;
    }

    if (meeting_start.format('YYYY-MM-DD') !== meeting_end.format('YYYY-MM-DD')) {
      return null;
    }

    const meeting_start_minutes = meeting_start.hour() * 60 + meeting_start.minute();
    const meeting_end_minutes = meeting_end.hour() * 60 + meeting_end.minute();
    const periods = form_value[day.key] ?? [];
    const is_meeting_fully_covered = periods.some(period => {
      const period_start = this.timeToMinutes(period?.start_time ?? '');
      const period_end = this.timeToMinutes(period?.end_time ?? '');
      return (
        !Number.isNaN(period_start) &&
        !Number.isNaN(period_end) &&
        period_start <= meeting_start_minutes &&
        period_end >= meeting_end_minutes
      );
    });

    if (is_meeting_fully_covered) {
      return null;
    }

    return {
      day_key: day.key,
      from_time: meeting_start.format('HH:mm'),
      to_time: meeting_end.format('HH:mm'),
    };
  }

  // Form initialization/mapping
  private fillFormFromSchedule(schedule: WorkingContractWeeklySchedule): void {
    const periods = schedule.working_contract_weekly_schedule_day_periods ?? [];
    for (const day of this.days_config) {
      const day_periods = periods.filter(p => p.day_of_week === day.key);
      const periods_arr = this.getPeriods(day.key);
      periods_arr.clear();
      for (const period of day_periods) {
        const group = this.fb.group({
          start_time: [this.toTimeString(period.start_time), Validators.required],
          end_time: [this.toTimeString(period.end_time), Validators.required],
          is_new: [false],
          original_end_time: [this.toTimeString(period.end_time)],
        });
        this.setPeriodControlsDisabledForDay(group, day.key);
        periods_arr.push(group);
      }
    }
    const remote_days = this.getRemoteWorkDaysFromSchedule(schedule);
    this.remote_work_days_control.setValue(remote_days);
  }

  // Unsaved changes snapshot
  private buildScheduleSnapshot(): string {
    const ordered_day_periods = this.days_config.map(day => {
      const periods = this.getPeriods(day.key).controls.map((ctrl: unknown) => {
        const group = ctrl as FormGroup;
        return {
          start_time: this.normalizeTimeToHHmm(group.get('start_time')?.value ?? ''),
          end_time: this.normalizeTimeToHHmm(group.get('end_time')?.value ?? ''),
        };
      });

      return {
        day_of_week: day.key,
        periods,
      };
    });

    return JSON.stringify({
      schedule_mode: this.schedule_mode(),
      day_periods: ordered_day_periods,
      remote_work_days: this.is_normal_mode() ? [...this.selected_remote_work_days()].sort() : [],
    });
  }

  private fillFormFromDayPeriods(periods: Array<{ day_of_week: DayOfWeekEnum | string; start_time: string; end_time: string }>): void {
    for (const day of this.days_config) {
      const day_periods = periods.filter(p => p.day_of_week === day.key);
      const periods_arr = this.getPeriods(day.key);
      periods_arr.clear();
      for (const period of day_periods) {
        const group = this.fb.group({
          start_time: [this.toTimeString(period.start_time), Validators.required],
          end_time: [this.toTimeString(period.end_time), Validators.required],
          is_new: [false],
          original_end_time: [this.toTimeString(period.end_time)],
        });
        this.setPeriodControlsDisabledForDay(group, day.key);
        periods_arr.push(group);
      }
    }
  }

  // Remote work mapping
  private getRemoteWorkDaysFromSchedule(schedule: WorkingContractWeeklySchedule): DayOfWeekEnum[] {
    const result: DayOfWeekEnum[] = [];
    if (schedule.has_remote_work_monday) result.push(DayOfWeekEnum.Monday);
    if (schedule.has_remote_work_tuesday) result.push(DayOfWeekEnum.Tuesday);
    if (schedule.has_remote_work_wednesday) result.push(DayOfWeekEnum.Wednesday);
    if (schedule.has_remote_work_thursday) result.push(DayOfWeekEnum.Thursday);
    if (schedule.has_remote_work_friday) result.push(DayOfWeekEnum.Friday);
    if (schedule.has_remote_work_saturday) result.push(DayOfWeekEnum.Saturday);
    if (schedule.has_remote_work_sunday) result.push(DayOfWeekEnum.Sunday);
    return result;
  }

  // UI helpers
  private toTimeString(value: string): string {
    if (value === null || value === undefined) return '09:00';
    if (value.length >= 5) return value.slice(0, 5);
    return value;
  }

  private syncPeriodStartedLock(periodGroup: FormGroup, day_key: DayOfWeekEnum, period_index: number): void {
    if (!this.is_week_exception_mode() || this.isDayDisabledInWeekException(day_key)) {
      return;
    }

    const start = periodGroup.get('start_time');
    const end = periodGroup.get('end_time');

    if (this.is_free_form_active()) {
      start?.enable();
      end?.enable();
      return;
    }

    const is_new_period = periodGroup.get('is_new')?.value === true;
    const is_fully_ended = this.isCurrentDayEndedPeriod(day_key, period_index);

    // Brand-new franjas that landed fully in the past must remain editable.
    if (is_new_period && is_fully_ended) {
      start?.enable();
      end?.enable();
      return;
    }

    if (this.isCurrentDayInWeekException(day_key)) {
      const should_lock_period_start = this.isCurrentDayStartedPeriod(day_key, period_index);
      if (should_lock_period_start) {
        start?.disable();
      } else {
        start?.enable();
      }
      end?.enable();
      return;
    }

    const should_lock_period_end = this.isCurrentDayEndedPeriod(day_key, period_index, true);
    const should_lock_period_start = this.isCurrentDayStartedPeriod(day_key, period_index);

    if (should_lock_period_end) {
      start?.disable();
      end?.disable();
      return;
    }

    if (should_lock_period_start) {
      start?.disable();
      end?.enable();
      return;
    }

    start?.enable();
    end?.enable();
  }

  private isNewPeriod(day_key: DayOfWeekEnum, period_index: number): boolean {
    const period_group = this.getPeriods(day_key).at(period_index) as FormGroup | null;

    return period_group?.get('is_new')?.value === true;
  }

  private isCurrentDayStartedPeriod(day_key: DayOfWeekEnum, period_index: number): boolean {
    if (!this.is_week_exception_mode() || this.free_form_mode() || !this.data.start_week_date) {
      return false;
    }

    const week_start = dayjs(this.data.start_week_date).startOf('day');
    const day_offset = dayOfWeekIso(day_key) - 1;
    const that_day_in_week = week_start.add(day_offset, 'day').startOf('day');
    const now = dayjs();
    const today = now.startOf('day');

    if (!that_day_in_week.isSame(today)) {
      return false;
    }

    const periods_arr = this.getPeriods(day_key);
    const period_group = periods_arr.at(period_index) as FormGroup | null;
    if (period_group == null) {
      return false;
    }

    const start_time = this.normalizeTimeToHHmm(period_group.get('start_time')?.value ?? '');
    const start_minutes = this.timeToMinutes(start_time);
    if (Number.isNaN(start_minutes)) {
      return false;
    }

    const now_minutes = now.hour() * 60 + now.minute();
    return now_minutes >= start_minutes;
  }

  private isCurrentDayEndedPeriod(day_key: DayOfWeekEnum, period_index: number, should_sum_grace_time = false): boolean {
    if (!this.is_week_exception_mode() || this.free_form_mode() || !this.data.start_week_date) {
      return false;
    }

    const week_start = dayjs(this.data.start_week_date).startOf('day');
    const day_offset = dayOfWeekIso(day_key) - 1;
    const that_day_in_week = week_start.add(day_offset, 'day').startOf('day');
    const now = dayjs();
    const today = now.startOf('day');

    if (!that_day_in_week.isSame(today)) {
      return false;
    }

    const periods_arr = this.getPeriods(day_key);
    const period_group = periods_arr.at(period_index) as FormGroup | null;
    if (period_group == null) {
      return false;
    }

    const end_time = this.normalizeTimeToHHmm(period_group.get('end_time')?.value ?? '');
    const end_minutes = this.timeToMinutes(end_time);
    if (Number.isNaN(end_minutes)) {
      return false;
    }

    const grace_minutes = should_sum_grace_time ? this.end_time_grace_minutes() : 0;
    const now_minutes = now.hour() * 60 + now.minute();
    return now_minutes >= end_minutes + grace_minutes;
  }

  private dayHasStartedPeriodLocked(day_key: DayOfWeekEnum): boolean {
    const periods_arr = this.getPeriods(day_key);
    for (let period_index = 0; period_index < periods_arr.length; period_index++) {
      if (this.isCurrentDayStartedPeriod(day_key, period_index)) {
        return true;
      }
    }

    return false;
  }

  private computeDaysWithPastTimeInputsForToday(value: FormValue): Partial<Record<DayOfWeekEnum, boolean>> {
    const result: Partial<Record<DayOfWeekEnum, boolean>> = {};

    if (!this.is_week_exception_mode() || this.free_form_mode() || value == null || typeof value !== 'object') {
      return result;
    }

    const now = dayjs();
    const now_minutes = now.hour() * 60 + now.minute();
    const grace_minutes = this.end_time_grace_minutes();

    for (const day of this.days_config) {
      if (!this.isCurrentDayInWeekException(day.key) || this.isDayDisabledInWeekException(day.key)) {
        continue;
      }

      const periods = value[day.key];
      if (!Array.isArray(periods)) {
        continue;
      }

      let has_past_time = false;
      for (let period_index = 0; period_index < periods.length; period_index++) {
        const period = periods[period_index];
        const start_minutes = this.timeToMinutes(this.normalizeTimeToHHmm(period?.start_time ?? ''));
        const end_minutes = this.timeToMinutes(this.normalizeTimeToHHmm(period?.end_time ?? ''));
        const period_group = this.getPeriods(day.key).at(period_index) as FormGroup | null;
        const is_new_period = period_group?.get('is_new')?.value === true;
        const is_start_locked = this.isPeriodStartLockedInWeekException(day.key, period_index);
        const original_end = this.normalizeTimeToHHmm(period_group?.get('original_end_time')?.value ?? '');
        const original_end_minutes = this.timeToMinutes(original_end);
        const is_past_grace_on_original =
          !is_new_period && !Number.isNaN(original_end_minutes) && now_minutes >= original_end_minutes + grace_minutes;

        if (is_new_period && !Number.isNaN(end_minutes) && end_minutes <= now_minutes) {
          has_past_time = true;
          break;
        }

        if (is_past_grace_on_original) {
          const end_after_now = !Number.isNaN(end_minutes) && end_minutes > now_minutes;
          const end_before_original = !Number.isNaN(end_minutes) && end_minutes < original_end_minutes;
          if (end_after_now || end_before_original) {
            has_past_time = true;
            break;
          }
          continue;
        }

        const has_invalid_start = !is_start_locked && !Number.isNaN(start_minutes) && start_minutes < now_minutes;
        const end_moved_into_past =
          !Number.isNaN(end_minutes) &&
          end_minutes < now_minutes &&
          (is_new_period || Number.isNaN(original_end_minutes) || end_minutes !== original_end_minutes);

        if (has_invalid_start || end_moved_into_past) {
          has_past_time = true;
          break;
        }
      }

      if (has_past_time) {
        result[day.key] = true;
      }
    }

    return result;
  }

  private hasPastTimeInputInTodayNow(): boolean {
    const form_value = this.form.getRawValue() as FormValue;
    const days_with_invalid_times = this.computeDaysWithPastTimeInputsForToday(form_value);

    return Object.values(days_with_invalid_times).some(Boolean);
  }

  private isCurrentDayInWeekException(day_key: DayOfWeekEnum): boolean {
    if (!this.is_week_exception_mode() || !this.data.start_week_date) {
      return false;
    }

    const week_start = dayjs(this.data.start_week_date).startOf('day');
    const day_offset = dayOfWeekIso(day_key) - 1;
    const that_day_in_week = week_start.add(day_offset, 'day').startOf('day');
    const today = dayjs().startOf('day');

    return that_day_in_week.isSame(today);
  }
}

type FormValue = Record<DayOfWeekEnum, { start_time: string; end_time: string; is_new?: boolean; original_end_time?: string | null }[]>;

type MeetingCoverageConflict = { day_key: DayOfWeekEnum; from_time: string; to_time: string };
